import { RoutePath, RouteData, RouteDataWithUniqueFlights, RouteFlightWithId } from './mapTypes'

export const generateMidpointLatLng = (latlng1: [number, number], latlng2: [number, number]) => {
  ;[latlng1, latlng2] = latlng1[1] > latlng2[1] ? [latlng2, latlng1] : [latlng1, latlng2]
  const offsetX = latlng2[1] - latlng1[1]
  const offsetY = latlng2[0] - latlng1[0]
  const offset = 7
  const c = Math.sqrt(Math.pow(offsetX, 2) + Math.pow(offsetY, 2))
  const theta = Math.atan2(offsetY, offsetX)
  const thetaOffset = 3.14 / offset
  const c2 = c / 2 / Math.cos(thetaOffset)
  const theta2 = theta + thetaOffset
  const midpointX = c2 * Math.cos(theta2) + latlng1[1]
  const midpointY = c2 * Math.sin(theta2) + latlng1[0]
  return [midpointY, midpointX] as [number, number]
}

export const uniqifyPaths = (paths: RoutePath[][]) => {
  const hashTable: Record<string, boolean> = {}
  return paths.map((path) =>
    path.filter((pathObj) => {
      if (hashTable[pathObj.lat + pathObj.lon]) return false
      hashTable[pathObj.lat + pathObj.lon] = true
      return true
    })
  )
}

export const uniqifyFlights = (flights: RouteFlightWithId[][]) => {
  const hashTable:Record<string,boolean> = {}
  return flights.map((flight) => flight.filter((flightObj) => {
    if(hashTable[flightObj.id]) return false
    hashTable[flightObj.id] = true
    return true
  }))
}

export const addFlightId = (routes: RouteData[]): RouteDataWithUniqueFlights[] =>
  routes.map((routeObj) => ({
    ...routeObj,
    flights: routeObj.flights.map((flight, i) => ({
      ...flight,
      id: routeObj.path[i].lon + routeObj.path[i].lat + flight.from + flight.to,
    })),
  }))

export const addConnectorFlight = (routes: RouteData[]): RouteData[] =>
  routes.map((routeObj) => ({
    ...routeObj,
    flights: routeObj.flights.map((flight) => {
      if (flight.distance) return flight
      const [from, to] = flight.code.split('-')
      return { ...flight, from, to }
    }),
  }))

export const changePathLocation = (routes: RouteData[]): RouteData[] => {
  const WEST_EXTREME = -160
  const EAST_EXTREME = 360 + WEST_EXTREME

  const pathCopy = routes.map(({ path }) => path)
  for (let j = 0; j < pathCopy.length; j++) {
    for (let i = 1; i < pathCopy[j].length; i++) {
      const lon1 = pathCopy[j][i - 1].lon
      const lon2 = pathCopy[j][i].lon

      const lonsSign = [Math.sign(lon1), Math.sign(lon2)]
      const isSameSign = lonsSign.every((s) => s === -1) || lonsSign.every((s) => s === 1)

      const offsetWestLon2 = WEST_EXTREME - lon2
      const offsetEastLon2 = EAST_EXTREME - lon2
      const newLon2EastCross = EAST_EXTREME + Math.abs(offsetWestLon2)
      const newLon2WestCross = WEST_EXTREME - offsetEastLon2
      const offsetEast = newLon2EastCross - lon1
      const offsetWest = lon1 > 0 ? newLon2WestCross - lon1 : Math.abs(newLon2WestCross) + lon1
      const offset = isSameSign
        ? lon1 - lon2
        : Math.min(lon1, lon2) - Math.max(lon1, lon2)
      const minOffset = Math.min(Math.abs(offset), Math.abs(offsetEast), Math.abs(offsetWest))
      const lon =
        minOffset === Math.abs(offset) ? lon2 : minOffset === Math.abs(offsetEast) ? newLon2EastCross : newLon2WestCross
      pathCopy[j][i] = { ...pathCopy[j][i], lon }
    }
  }

  return routes.map((route, i) => ({ ...route, path: pathCopy[i] }))
}

export const isTouchDevice = () => {
  const deviceAgent = navigator.userAgent
  const checkDevice = () => {
    return !!(deviceAgent.match(/(iphone|ipod|ipad)/) ||
      deviceAgent.match(/(android)/)  || 
      deviceAgent.match(/(iemobile)/) || 
      deviceAgent.match(/iphone/i) || 
      deviceAgent.match(/ipad/i) || 
      deviceAgent.match(/ipod/i) || 
      deviceAgent.match(/blackberry/i) || 
      deviceAgent.match(/bada/i)) || false
  }
  return window && navigator && (('ontouchstart' in window) ||
  (navigator.maxTouchPoints > 0) || checkDevice());
}
